import React, {FormEvent} from 'react';
import HomeModalShell from '../../home/components/HomeModalShell';
import homeStyles from '../../home/Home.module.css';

export const PROFILE_NAME_MAX_LENGTH = 100;

interface ProfileNameEditorModalProps {
  isOpen: boolean;
  value: string;
  loading: boolean;
  error: string | null;
  validationError: string | null;
  onChange: (value: string) => void;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

const ProfileNameEditorModal: React.FC<ProfileNameEditorModalProps> = ({
  isOpen,
  value,
  loading,
  error,
  validationError,
  onChange,
  onClose,
  onSubmit,
}) => {
  if (!isOpen) {
    return null;
  }

  const messageId = validationError
    ? 'profile-name-validation-error'
    : error
      ? 'profile-name-save-error'
      : undefined;

  return (
    <HomeModalShell
      backdropClassName={homeStyles.modalBackdrop}
      panelClassName={homeStyles.modalCard}
      ariaLabelledBy="profile-name-editor-title"
      ariaDescribedBy="profile-name-editor-description"
      onClose={onClose}
      closeDisabled={loading}
    >
      <div className={homeStyles.modalHeader}>
        <div>
          <h2 id="profile-name-editor-title" className={homeStyles.modalTitle}>
            Edit Profile
          </h2>
          <p id="profile-name-editor-description" className={homeStyles.modalDescription}>
            Update the name displayed on your profile.
          </p>
        </div>
        <button
          type="button"
          className={homeStyles.modalClose}
          onClick={onClose}
          disabled={loading}
          aria-label="Close profile editor"
        >
          &times;
        </button>
      </div>

      <form onSubmit={onSubmit} className={homeStyles.modalForm} noValidate>
        <label className={homeStyles.modalLabel} htmlFor="profile-name-input">
          Name
          <span className={homeStyles.modalLabelHint}>
            {value.length}/{PROFILE_NAME_MAX_LENGTH} characters
          </span>
        </label>
        <input
          id="profile-name-input"
          className={homeStyles.modalInput}
          type="text"
          autoComplete="name"
          value={value}
          maxLength={PROFILE_NAME_MAX_LENGTH}
          aria-describedby={messageId}
          aria-invalid={Boolean(validationError || error)}
          onChange={(event) => onChange(event.target.value)}
          autoFocus
          disabled={loading}
        />

        {validationError ? (
          <div
            id="profile-name-validation-error"
            className={`${homeStyles.modalMessage} ${homeStyles.modalValidationMessage}`}
            role="alert"
          >
            {validationError}
          </div>
        ) : null}

        {error ? (
          <div
            id="profile-name-save-error"
            className={`${homeStyles.modalMessage} ${homeStyles.modalErrorMessage}`}
            role="alert"
          >
            {error}
          </div>
        ) : null}

        <div className={homeStyles.modalActions}>
          <button
            type="button"
            className={`${homeStyles.modalButton} ${homeStyles.modalButtonSecondary}`}
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`${homeStyles.modalButton} ${homeStyles.modalButtonPrimary}`}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Name'}
          </button>
        </div>
      </form>
    </HomeModalShell>
  );
};

export default React.memo(ProfileNameEditorModal);
